import {Navigate, useSearchParams} from 'react-router'
import {fromJust, mmap} from '@freckle/maybe'
import {Languages} from '@freckle/educator-entities/ts/common/helpers/languages'
import {parseQueryParamNumArray} from '@freckle/educator-entities/ts/common/helpers/routers/query-params'
import {useAsync} from '@freckle/educator-entities/ts/common/hooks/use-async/index'
import {useSchools} from '@freckle/educator-entities/ts/common/hooks/use-schools'
import {useTeachersAsASchoolAdmin} from '@freckle/educator-entities/ts/common/hooks/use-teacher-as-a-school-admin'
import type {RlDomainT} from '@freckle/educator-entities/ts/common/models/rl-domain'
import {
  domainHasGrade,
  domainGrades,
  firstDomainCoveringGrade,
} from '@freckle/educator-entities/ts/common/models/rl-domain'
import {fetchRlDomains} from '@freckle/educator-entities/ts/common/models/rl-standard'
import {fetchRlStandardSets} from '@freckle/educator-entities/ts/common/models/rl-standard-set'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import type {BaseTeacherAttrs} from '@freckle/educator-entities/ts/users/models/teacher'
import {WithResources} from '@freckle/school/ts/common/components/with-resources'
import type {SummaryT} from '@freckle/school/ts/common/models/summary'
import {fetchSummary} from '@freckle/school/ts/common/models/summary'
import {Routes} from '@freckle/school/ts/common/routers/routes'
import {useSchoolAdmin} from '@freckle/school/ts/hooks/use-school-admin'
import {useStudents} from '@freckle/school/ts/hooks/use-students'
import type {IdentityProviderOrNoneT} from '@freckle/school/ts/manage-teachers/api-helpers/filters'
import {parseDefaultIdpsFromQueryParam} from '@freckle/school/ts/manage-teachers/api-helpers/filters'
import {fetchProficiencyReportsForSchools} from '@freckle/school/ts/proficiency-report/models/proficiency-report'
import {indexStudentsById} from '@freckle/school/ts/users/collections/students'
import {getGradeWithMostStudents} from '@freckle/school/ts/users/collections/students'
import type {SchoolAdminAttrs} from '@freckle/school/ts/users/models/school-admin'
import {SchoolProficiencyReport} from './school-proficiency-report'

export default function SchoolProficiencyReportHandler() {
  const schools = useSchools()
  const students = useStudents()
  const teachers = useTeachersAsASchoolAdmin()
  const schoolAdmin = useSchoolAdmin()

  return (
    <WithResources
      resources={{
        schools,
        students,
        teachers,
        schoolAdmin,
      }}
      render={({schools, students, teachers, schoolAdmin}) => (
        <Inner
          schools={schools}
          students={students}
          teachers={teachers}
          schoolAdmin={schoolAdmin}
        />
      )}
    />
  )
}

type CommonProps = {
  schools: SchoolAttrs[]
  students: StudentAttrs[]
  teachers: BaseTeacherAttrs[]
  schoolAdmin: SchoolAdminAttrs
}

type InnerPropsT = CommonProps

function Inner(props: InnerPropsT) {
  const {schools, students, schoolAdmin} = props
  const [searchParams, _setSearchParams] = useSearchParams()
  const domainId = searchParams.get('domainId')
  const grade = searchParams.get('grade')
  const identityProviders = searchParams.get('identityProviders')
  const schoolsIds = searchParams.get('schoolsIds')
  const paramsSchoolsIds = schoolsIds !== null ? parseQueryParamNumArray(schoolsIds) : []
  const domains = useAsync(
    () => fetchRlDomains('math', schoolAdmin.mathStandardSet, Languages.En),
    'fetchRlDomains',
    ['math', schoolAdmin.mathStandardSet, Languages.En],
  )

  // Selected Schools
  const defaultSchoolsIds = schools.map(s => s.id)
  const selectedSchoolsIds = paramsSchoolsIds.length === 0 ? defaultSchoolsIds : paramsSchoolsIds

  const summary = useAsync(() => fetchSummary(schoolAdmin.id, selectedSchoolsIds), 'fetchSummary', [
    schoolAdmin.id,
    selectedSchoolsIds,
  ])

  return (
    <WithResources
      resources={{
        domains,
        summary,
      }}
      render={({domains, summary}) => {
        const domainGradeCoverage = new Set(domains.flatMap(domainGrades))
        const selectedGrade =
          grade !== null
            ? parseInt(grade, 10)
            : calculateDefaultGrade(students, domainGradeCoverage)
        // Selected DomainId
        const relevantDomains = domains.filter(domain => domainHasGrade(domain, selectedGrade))
        const mDefaultDomainId = mmap(
          domain => domain.id,
          firstDomainCoveringGrade(relevantDomains, selectedGrade),
        )
        const defaultDomainId = fromJust(mDefaultDomainId, `No domains for ${selectedGrade}`)
        const selectedDomainId =
          domainId !== null && relevantDomains.find(dom => dom.id === domainId) !== undefined
            ? domainId
            : defaultDomainId

        const selectedIdentityProviders = parseDefaultIdpsFromQueryParam(
          identityProviders,
          {
            tag: 'for-student',
            ...summary,
          },
          {tag: 'all-schools', allSchools: schools},
        )

        return (
          <InnerMore
            domains={domains}
            domainGradeCoverage={domainGradeCoverage}
            summary={summary}
            selectedGrade={selectedGrade}
            selectedSchoolsIds={selectedSchoolsIds}
            selectedDomainId={selectedDomainId}
            selectedIdentityProviders={selectedIdentityProviders}
            paramsSchoolsIds={paramsSchoolsIds}
            {...props}
          />
        )
      }}
    />
  )
}

type InnerMorePropsT = CommonProps & {
  paramsSchoolsIds: Array<number>
  domains: RlDomainT[]
  domainGradeCoverage: Set<number>
  summary: SummaryT
  selectedGrade: number
  selectedSchoolsIds: number[]
  selectedDomainId: string
  selectedIdentityProviders: IdentityProviderOrNoneT[]
}

function InnerMore(props: InnerMorePropsT) {
  const {
    teachers,
    students,
    schools,
    domains,
    domainGradeCoverage,
    schoolAdmin,
    paramsSchoolsIds,
    summary,
    selectedGrade,
    selectedSchoolsIds,
    selectedDomainId,
    selectedIdentityProviders,
  } = props

  const proficiencyReports = useAsync(
    () =>
      fetchProficiencyReportsForSchools(selectedSchoolsIds, {
        grade: selectedGrade,
        domainId: selectedDomainId,
        identityProviders: selectedIdentityProviders,
      }),
    'fetchProficiencyReportsForSchools',
    [
      selectedSchoolsIds,
      {
        grade: selectedGrade,
        domainId: selectedDomainId,
        identityProviders: selectedIdentityProviders,
      },
    ],
  )
  const mathStandardSetId = schoolAdmin.mathStandardSet.id

  const mathStandardSet = useAsync(
    () =>
      fetchRlStandardSets({'content-area': 'math', 'standard-set': mathStandardSetId}).then(
        standardSets => standardSets[0] ?? null,
      ),
    'fetchRlStandardSet',
    ['math', mathStandardSetId],
  )

  return (
    <WithResources
      resources={{proficiencyReports, mathStandardSet}}
      render={({proficiencyReports, mathStandardSet}) => {
        if (mathStandardSet === null) {
          return <Navigate to={Routes.dashboard()} replace />
        }
        return (
          <SchoolProficiencyReport
            teachers={teachers}
            students={indexStudentsById(students)}
            schools={schools}
            domains={domains}
            domainGradeCoverage={domainGradeCoverage}
            mathStandardSet={mathStandardSet}
            selectedSchoolsIds={selectedSchoolsIds}
            paramsSchoolsIds={paramsSchoolsIds}
            selectedDomainId={selectedDomainId}
            selectedGrade={selectedGrade}
            selectedIdentityProviders={selectedIdentityProviders}
            proficiencyReportData={proficiencyReports}
            summary={summary}
          />
        )
      }}
    />
  )
}

const MAX_DEFAULT_GRADE = 8

function calculateDefaultGrade(
  students: Array<StudentAttrs>,
  domainGradeCoverage: Set<number>,
): number {
  const gradeWithMostStudents = getGradeWithMostStudents(students, domainGradeCoverage)

  let defaultGrade = domainGradeCoverage.has(MAX_DEFAULT_GRADE)
    ? MAX_DEFAULT_GRADE
    : Array.from(domainGradeCoverage)[0]

  if (defaultGrade === undefined) {
    console.error('Failed to find default grade, domains must have no coverage', {
      gradeWithMostStudents,
      domainGradeCoverage,
    })
    defaultGrade = MAX_DEFAULT_GRADE
  }

  return gradeWithMostStudents === null
    ? defaultGrade
    : Math.min(gradeWithMostStudents, defaultGrade)
}
